/**
 * Reading a change order's trail (0046), and the client's questions in it.
 *
 * The trail is the only place the FIRST send survives: `sent_at` holds one
 * timestamp, so a withdraw-and-resend erases the date someone asks about when
 * they say "you never sent me that". This module turns those rows into lines a
 * panel renders, and answers the one question the list needs — has a client
 * asked something nobody has answered?
 *
 * Pure. Everything here takes rows and returns strings or booleans.
 */

import type { ChangeOrderEventRow, ChangeOrderEventKind } from '@/types/database';

/** The fields this module reads — a row, or a portal payload's question. */
export type EventFields = Pick<ChangeOrderEventRow,
  'id' | 'change_order_id' | 'kind' | 'at' | 'actor_name' | 'via' | 'note'>;

/**
 * What each kind reads as, in the designer's own voice.
 *
 * 'acknowledged' is stored and "Approved" is displayed (CLAUDE.md), and the
 * same split applies here: 'withdrawn' is what a cancel is called, because
 * "Cancelled" is the status and withdrawing is the act.
 */
const VERB: Record<ChangeOrderEventKind, string> = {
  created: 'Created',
  sent: 'Sent to the client',
  approved: 'Approved',
  declined: 'Declined by the client',
  withdrawn: 'Withdrawn',
  reopened: 'Reopened as a draft',
  revised: 'Revised — the approved version was filed into its history',
  deleted: 'Moved to Deleted',
  restored: 'Restored from Deleted',
  question_asked: 'Question from the client',
  question_answered: 'Answered',
};

export function eventVerb(kind: ChangeOrderEventKind): string {
  return VERB[kind] ?? kind;
}

/**
 * "Sep 20, 2026 at 3:00 AM".
 *
 * `at` is a `timestamptz`, so localizing it to the viewer is the point and
 * `new Date()` is correct here — unlike a date-only column (CLAUDE.md).
 *
 * Not `toLocaleString()`, which is what this was: nine of those stacked in one
 * panel read as "9/20/2026, 3:00:00 AM" nine times over, and the seconds on a
 * change order's history are noise. The formatter stays injectable so a test
 * can assert an exact string rather than whatever locale the machine has.
 */
const stamp = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
};

/**
 * One line of the history.
 *
 * Deliberately no note text: the only two kinds that carry any are a question
 * and an answer, and those have their own block directly above the history in
 * the panel. Carrying it here printed every question twice, three inches
 * apart, which is how it looked the first time it was rendered in a browser.
 */
export interface TimelineEntry {
  id: string;
  kind: ChangeOrderEventKind;
  /** "Approved · Sep 30, 2026 at 2:14 PM". */
  headline: string;
  /** "by Jane Anderson, in the client portal", or '' when nobody is named. */
  attribution: string;
}

/**
 * The trail for one change order, OLDEST first.
 *
 * Oldest first on purpose: a history is read forward, and the revision list
 * next to it (buildHistory) is newest first because a revision is read against
 * what replaced it. Different questions, different orders.
 */
export function buildTimeline(
  events: readonly EventFields[], changeOrderId: string, fmt: (iso: string) => string = stamp,
): TimelineEntry[] {
  return events
    // .filter() already returns a new array, so the sort below never touches
    // the caller's — this list is the workspace's, shared with the whole page.
    .filter((e) => e.change_order_id === changeOrderId)
    .sort((a, b) => a.at.localeCompare(b.at))
    .map((e) => ({
      id: e.id,
      kind: e.kind,
      headline: `${eventVerb(e.kind)} · ${fmt(e.at)}`,
      attribution: attribution(e),
    }));
}

/**
 * "by Jane Anderson, in the client portal".
 *
 * `via` is the database's word, not the browser's (0046): the designer's own
 * tab cannot claim a client signed something. A designer-recorded approval
 * says so rather than pretending to be the client's.
 */
export function attribution(e: Pick<EventFields, 'kind' | 'actor_name' | 'via'>): string {
  const who = (e.actor_name ?? '').trim();
  const where = e.via === 'portal' ? 'in the client portal'
    : e.via === 'designer' ? 'recorded by the designer'
    : '';
  if (who && where) return `by ${who}, ${where}`;
  if (who) return `by ${who}`;
  if (where) return where;
  return '';
}

/**
 * The client's questions on one change order, oldest first, each paired with
 * the answers that came AFTER it.
 *
 * Pairing by time rather than by an id: there is no reply-to column, and
 * adding one would invite the app to write the trail. Two questions in a row
 * followed by one answer therefore attach that answer to the second — which
 * is what a conversation reads like anyway.
 */
export interface QuestionThread {
  question: EventFields;
  answers: EventFields[];
}

export function questionThreads(
  events: readonly EventFields[], changeOrderId: string,
): QuestionThread[] {
  const ordered = events
    .filter((e) => e.change_order_id === changeOrderId
      && (e.kind === 'question_asked' || e.kind === 'question_answered'))
    .sort((a, b) => a.at.localeCompare(b.at));

  const threads: QuestionThread[] = [];
  for (const e of ordered) {
    if (e.kind === 'question_asked') threads.push({ question: e, answers: [] });
    else if (threads.length > 0) threads[threads.length - 1]!.answers.push(e);
    // An answer with no question before it is orphaned history — nothing in
    // the product can make one, and dropping it beats inventing a question.
  }
  return threads;
}

/** A question nobody has answered yet. The flag the designer needs to see. */
export function unansweredCount(events: readonly EventFields[], changeOrderId: string): number {
  return questionThreads(events, changeOrderId).filter((t) => t.answers.length === 0).length;
}

/** Change order ids with at least one unanswered question, for a list badge. */
export function changeOrdersAwaitingAnswer(events: readonly EventFields[]): Set<string> {
  const ids = new Set(events.map((e) => e.change_order_id));
  const out = new Set<string>();
  for (const id of ids) if (unansweredCount(events, id) > 0) out.add(id);
  return out;
}

/** "1 question waiting" / "3 questions waiting". */
export function awaitingAnswerLabel(n: number): string | null {
  if (n <= 0) return null;
  return `${n} question${n === 1 ? '' : 's'} waiting`;
}
